import { useEffect, useState } from 'react';
import { parseSvgColors, applyColors } from './svgColors';

// url -> Promise<{ text, slots }>. Each SVG is fetched and parsed once.
const cache = new Map();

function loadSvg(url) {
    if (!cache.has(url)) {
        const promise = fetch(url)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
                return r.text();
            })
            .then((text) => ({ text, slots: parseSvgColors(text) }));
        promise.catch(() => cache.delete(url));
        cache.set(url, promise);
    }
    return cache.get(url);
}

/** Fetches + parses an SVG. Returns { text, slots } once loaded, otherwise null. */
export function useSvgAsset(url) {
    const [loaded, setLoaded] = useState({ url: null, asset: null });

    useEffect(() => {
        if (!url) return undefined;
        let live = true;
        loadSvg(url)
            .then((asset) => { if (live) setLoaded({ url, asset }); })
            .catch((err) => console.warn('UniformBuilder: failed to load', url, err));
        return () => { live = false; };
    }, [url]);

    return url && loaded.url === url ? loaded.asset : null;
}

/**
 * Blob URL for `asset` recoloured with `colors` (string[] parallel to
 * asset.slots). With `mask` every slot is painted white, giving an alpha mask of
 * the shape. Returns null until `asset` is available.
 */
export function useRecoloredUrl(asset, colors, mask = false) {
    const [url, setUrl] = useState(null);
    const colorKey = colors ? colors.join(',') : '';

    useEffect(() => {
        if (!asset) {
            setUrl(null);
            return undefined;
        }
        const chosen = mask
            ? asset.slots.map(() => '#ffffff')
            : (colorKey ? colorKey.split(',') : []);
        const svg = applyColors(asset.text, asset.slots, chosen);
        const next = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [asset, colorKey, mask]);

    return url;
}
